import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader/PublicHeader';
import './DocsPage.css';

// Import SVG icons
import PlayIcon from '../../image-assets/icon.play.svg';
import RephraseIcon from '../../image-assets/icon.rephrase.svg';
import ToneIcon from '../../image-assets/icon.tone.svg';
import PlaceholdersIcon from '../../image-assets/icon.placeholders.svg';
import ImportIcon from '../../image-assets/icon.import.svg';
import ExportIcon from '../../image-assets/icon.export.svg';
import ApiIcon from '../../image-assets/icon.api.svg';
import ProfileIcon from '../../image-assets/icon.profile.svg';
import TasksIcon from '../../image-assets/icon.tasks.svg';

// Define the features based on code analysis
const features = [
  { id: 'introduction', title: 'Introduction', description: 'Get started and streamline your workflow.', iconUrl: PlayIcon },
  { id: 'ai-translate', title: 'Translation with AI', description: 'Quickly translate content using advanced AI models.', iconUrl: RephraseIcon },
  { id: 'tone-of-voice', title: 'Tone of Voice', description: 'Maintain brand consistency with AI-powered tone adjustments.', iconUrl: ToneIcon },
  { id: 'placeholders', title: 'Universal Placeholders', description: 'Manage dynamic content variables across platforms effortlessly.', iconUrl: PlaceholdersIcon },
  { id: 'reviews', title: 'Reviews', description: 'Streamline translation quality assurance with collaborative reviews.', iconUrl: TasksIcon },
  { id: 'import', title: 'Import Translations', description: 'Easily import existing localization files.', iconUrl: ImportIcon },
  { id: 'export', title: 'Export Translations', description: 'Generate platform-specific files for your apps.', iconUrl: ExportIcon },
  { id: 'api', title: 'Fetch with API', description: 'Fetch the latest translations programmatically.', iconUrl: ApiIcon },
  { id: 'collaboration', title: 'Collaboration', description: 'Manage team access and roles for secure collaboration.', iconUrl: ProfileIcon },
  // Removed writing features as they seem integrated into Tone/Translation
];

// Component to render content based on selected feature
const FeatureContent = ({ featureId }: { featureId: string }) => {
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by anchor onClick in feature list
  const navigateToFeature = (event: React.MouseEvent<HTMLAnchorElement>, targetFeatureId: string) => {
    event.preventDefault();
    navigate(`/docs/${targetFeatureId}`);
  };

  switch (featureId) {
    case 'introduction':
      return (
        <>
          <h1>Write Better, Translate Faster, Ship Smoother.</h1>
          <p className="docs-intro">
            Centralize your team's writing, translation, and localization workflow. Explore the features below to see how you can create high-quality multilingual content more efficiently.
          </p>
          {/* Overview Grid */}
          <div className="docs-overview-grid">
            {features
              .filter(feature => feature.id !== 'introduction') // Exclude intro itself
              .map(feature => (
                <Link
                  key={feature.id}
                  to={`/docs/${feature.id}`}
                  className="docs-overview-box"
                >
                  <img src={feature.iconUrl} alt="" className="docs-overview-icon" />
                  <div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                </Link>
            ))}
          </div>
        </>
      );
    case 'ai-translate':
      return (
        <section id="ai-translate" className="docs-section">
          <h1>Translation with AI</h1>
          <p className="feature-value">
            Leverage powerful AI models to rapidly translate your content into numerous languages, accelerating your localization process.
          </p>
          <h3>Integrated Translation</h3>
          <p>
            Initiate translations directly within the interface. You can select individual text strings or use the batch translation option to translate multiple keys simultaneously.
          </p>
          <h3>Contextual & Tone-Aware</h3>
          <p>
            Your project context and <Link to="/docs/tone-of-voice">Tone of Voice</Link> affect your translations, ensuring the output is not just literally correct but also appropriate for your product and brand.
          </p>
          <h3>Placeholder Preservation</h3>
          <p>
            <Link to="/docs/placeholders">Placeholders</Link> (like <code>%@</code>, <code>%1$s</code>, <code>{'{variable}'}</code>, <code>[variable]</code> etc.) are preserved exactly as they are in the source text during AI translation, preventing corruption of dynamic content.
          </p>
        </section>
      );
    case 'tone-of-voice': 
      return (
        <section id="tone-of-voice" className="docs-section">
          <h1>Tone of Voice</h1>
          <p className="feature-value">
            Ensure brand consistency and cultural appropriateness in AI-generated translations and other AI writing tasks.
          </p>

          <h2>What Defines a Tone of Voice?</h2>
          <p>
            Your "Tone of Voice" is the personality and style conveyed through writing. Defining it clearly helps the AI match your brand identity. Consider these key aspects:
          </p>

          <h4>Formality Level</h4>
          <p>Decide how formal or casual the language should be. Should it sound like a professional report, a friendly chat, or something else?</p>

          <h4>Brand Personality</h4>
          <p>Think about the traits your brand embodies. Is it playful, serious, witty, empathetic, authoritative, informative, or a combination?</p>

          <h4>Clarity and Conciseness</h4>
          <p>Specify if you prefer short, direct sentences focused on clarity, or if more descriptive and elaborate language is acceptable or desired.</p>
          
          <h4>Technicality</h4>
          <p>Determine the appropriate level of technical jargon. Should it be used freely for an expert audience, or simplified for broader understanding?</p>

          <h4>Audience Consideration</h4>
          <p>Who are you primarily speaking to? Language often needs adjustment for different expertise levels, age groups, or cultural backgrounds.</p>

          <h4>Sentiment and Emotion</h4>
          <p>Guide how the AI should handle expressing positive or negative concepts. Should it be direct, nuanced, optimistic, or cautious?</p>

          <h4>Use of Humor</h4>
          <p>Is humor appropriate for your brand? If so, define the style – should it be witty, dry, playful, or avoided altogether?</p>
          
          <p>
             Beyond describing these aspects, providing specific examples of preferred phrasing, vocabulary, and even things to avoid within the Product Context field is highly effective in guiding the AI.
          </p>

          <h2>Configuration</h2>
          <p>
            Define your brand's specific Tone of Voice guidelines and provide relevant Product Context within your Team Settings. The more detail you provide about the elements described above, the better the AI can match your desired style.
          </p>
          
          <h2>AI Instruction</h2>
          <p>
            Your configured Tone of Voice and Product Context are automatically considered by the AI during <Link to="/docs/ai-translate">translation tasks</Link>. This guides the AI to generate outputs that align better with your desired style and terminology.
          </p>
        </section>
      );
    case 'placeholders':
      return (
        <section id="placeholders" className="docs-section">
          <h1>Universal Placeholders</h1>
          <p className="feature-value">
            Manage dynamic content and variables accurately across different platforms without needing to manually adjust syntax in your source text.
          </p>
          
          <h3>Automatic Detection</h3>
          <p>
            Texta automatically detects common placeholder formats within your source strings, such as those used for iOS (e.g., <code>%@</code>, <code>%d</code>), Android (e.g., <code>%1$s</code>, <code>%2$d</code>), and common web frameworks (e.g., <code>{'{variable}'}</code>, <code>[variable]</code>). These detected placeholders are visually highlighted in the editor.
          </p>

          <h3>Preservation During Translation</h3>
          <p>
            Detected placeholders are automatically preserved during the <Link to="/docs/ai-translate">AI translation process</Link>; the AI is instructed not to alter them, ensuring your dynamic content remains intact.
          </p>

          <h3>Missing Placeholder Detection & Correction</h3>
          <p>
            To help ensure accuracy, discrepancies are highlighted when a placeholder from the source text is missing in a specific language's translation. You can fix a missing placeholder with a single click, inserting the correct one directly into the translation.
          </p>

          <h3>Automatic Conversion on Export/API Fetch</h3>
          <p>
            The key benefit is automatic conversion. When you <Link to="/docs/export">export translations</Link> or fetch them via the <Link to="/docs/api">API</Link>, you specify the desired output placeholder format (<code>ios</code>, <code>android</code>, or <code>web</code>). Texta then automatically converts the detected placeholders in your translations to the correct syntax required by that specific target format. This eliminates the need to maintain platform-specific syntax in your source strings.
          </p>
        </section>
      );
    case 'reviews': 
      return (
        <section id="reviews" className="docs-section">
          <h1>Reviews</h1>
          <p className="feature-value">
            Organize and streamline the quality assurance process for your translations involving multiple stakeholders.
          </p>
          <h3>Review Creation</h3>
          <p>
            Create dedicated "Reviews" that bundle specific sets of translation keys needing verification or approval. This allows focusing review efforts on particular feature updates or content sections.
          </p>
          <h3>Collaborative Workflow</h3>
          <p>
            Team members with <Link to="/docs/collaboration">Reviewer, Admin, or Owner roles</Link> can access reviews assigned to them. Within a review, they can edit translations, leave comments for discussion, and track the approval status of each key.
          </p>
          <h3>Language Completion Tracking</h3>
          <p>
             Reviews allow tracking which languages have been fully reviewed and marked as complete for the specific set of keys included in that review.
          </p>
        </section>
      );
    case 'import': 
       return (
         <section id="import" className="docs-section">
           <h1>Import Existing Translations</h1>
           <p className="feature-value">
             Bring your existing translations into Texta from various common localization file formats.
           </p>

           <h3>Import Various File Formats</h3>
           <p>
             Texta supports importing from standard localization files, allowing you to easily migrate your existing work. You can import translations from:
           </p>
           <ul>
             <li>JSON (supporting both flat key-value and nested structures)</li>
             <li>iOS <code>.strings</code> files</li>
             <li>Android <code>strings.xml</code> files</li>
           </ul>

           <h3>How to Import</h3>
           <p>
             Importing is initiated from the toolbar in the main Overview tab.
           </p>

         </section>
       );
    case 'export': 
        return (
          <section id="export" className="docs-section">
            <h1>Export Translations</h1>
            <p className="feature-value">
              Generate platform-specific localization files ready for integration into your applications and development workflow.
            </p>
            
            <h3>Supported Formats</h3>
            <p>
              Export your project's translations into formats directly usable by developers: JSON (with options for flat or nested structures), iOS <code>.strings</code> files, and Android <code>strings.xml</code> files.
            </p>

            <h3>How to Export</h3>
            <p>
              Exporting is initiated from the toolbar in the main Overview tab. You select the format, choose the language(s), configure options like placeholder format, and download the file or zip archive.
            </p>

            <h3>Placeholder Conversion & Formatting</h3>
            <p>
              Texta detects various placeholder formats (like <code>{'{var}'}</code>, <code>%@</code>) in your source text and converts them to the correct syntax for your chosen output format (iOS, Android, Web) automatically. This means that you will never have incorrect placeholder formats in your translations.
            </p>
            
            <h3>Automating Exports via API</h3>
            <p>
              For automated workflows, you can programmatically fetch translations using the Texta API instead of manual exports. <Link to="/docs/api">Learn more about using the API to fetch translations.</Link>
            </p>
          </section>
        );
    case 'api': 
        return (
          <section id="api" className="docs-section">
            <h1>Fetch translations with API</h1>
            <p className="feature-value">
              Automate your localization workflow by integrating translation management directly into your development processes. Instead of manually downloading files, you can use the API to fetch the latest translations programmatically, ideal for including in your application's build process.
            </p>

            <h2>Capabilities</h2>
            <p>
              The API provides flexibility in how you retrieve your translation data:
            </p>
            
            <h3>Choosing the File Format</h3>
            <p>
              You can specify the exact file format needed for your target platform. Use the <code>format</code> parameter in your request to choose between 
              <code>"json"</code> (for web applications), 
              <code>"strings"</code> (for iOS), or 
              <code>"android"</code> (for Android XML).
            </p>

            <h3>Matching Placeholder Styles</h3>
            <p>
              Ensure dynamic content works correctly by telling the API which placeholder syntax your target platform requires in the output. Set the <code>placeholderFormat</code> parameter to 
              <code>"web"</code> (e.g., outputs <code>{'{user}'}</code>), 
              <code>"ios"</code> (e.g., outputs <code>%@</code>), or 
              <code>"android"</code> (e.g., outputs <code>%1$s</code>). Texta will automatically convert detected placeholders from your source text to this specified output format.
            </p>

            <h3>Selecting Languages</h3>
            <p>
              You can fetch translations for a single language by providing its code (e.g., <code>"en"</code>, <code>"de"</code>) in the optional <code>language</code> parameter. If you omit this parameter, the API will conveniently package all languages in your project into a single zip archive for download.
            </p>

            <h2>Making an API Call</h2>
            <p>
              To retrieve translations, you send an authenticated request to the API endpoint using the <code>POST</code> method. You'll need to include your Project API Key for authentication and specify the desired output using parameters in a JSON request body.
            </p>
            
            <h3>Generic Request Structure (cURL)</h3>
            <pre><code>{
`curl -X POST https://api.texta.app/functions/v1/pull-translations \
     -H "x-api-key: YOUR_PROJECT_API_KEY" \
     -H "Content-Type: application/json" \
     -d '{ 
       "format": "<desired_format>",
       "placeholderFormat": "<desired_placeholder_style>",
       "language": "<optional_language_code>"
     }'`
            }</code></pre>

            <h3>Key Parameters</h3>
             <dl className="api-param-list">
               <dt><code>format</code></dt>
               <dd>
                 (Required) File type: <code>"json"</code>, <code>"strings"</code>, <code>"android"</code>.
               </dd>
               <dt><code>placeholderFormat</code></dt>
               <dd>
                 (Required) Specifies the desired output syntax for placeholders: <code>"web"</code>, <code>"ios"</code>, <code>"android"</code>.
               </dd>
               <dt><code>language</code></dt>
               <dd>
                 (Optional) Language code (e.g., <code>"en"</code>). Omit for a zip file of all languages.
               </dd>
             </dl>
            
            <h2>Examples</h2>
            <p>Here are specific examples using cURL showing how to combine the parameters for different results:</p>

            <h3>Example 1: Get English Translations as JSON (for Web)</h3>
            <pre><code>{
`curl -X POST https://api.texta.app/functions/v1/pull-translations \
     -H "x-api-key: YOUR_PROJECT_API_KEY" \
     -H "Content-Type: application/json" \
     -d '{ 
       "format": "json",
       "placeholderFormat": "web",
       "language": "en"
     }'`
            }</code></pre>
            <p><em>Response: The response body contains the JSON data directly.</em></p>

            <h3>Example 2: Get German Translations as iOS Strings</h3>
            <pre><code>{
`curl -X POST https://api.texta.app/functions/v1/pull-translations \
     -H "x-api-key: YOUR_PROJECT_API_KEY" \
     -H "Content-Type: application/json" \
     -d '{ 
       "format": "strings",
       "placeholderFormat": "ios",
       "language": "de"
     }'`
            }</code></pre>
            <p><em>Response: The response body contains the .strings file content directly.</em></p>

            <h3>Example 3: Get All Languages as Android XML (in a Zip File)</h3>
            <pre><code>{
`curl -X POST https://api.texta.app/functions/v1/pull-translations \
     -H "x-api-key: YOUR_PROJECT_API_KEY" \
     -H "Content-Type: application/json" \
     -d '{ 
       "format": "android",
       "placeholderFormat": "android"
       // language parameter is omitted
     }' \
     --output translations.zip`
            }</code></pre>
             <p><em>Response: A zip file named <code>translations.zip</code> containing XML files is downloaded.</em></p>

          </section>
        );
    case 'collaboration': 
         return (
           <section id="collaboration" className="docs-section">
             <h1>Collaboration</h1>
             <p className="feature-value">
               Manage team access and permissions to ensure secure and efficient collaboration on localization projects.
             </p>

             <h2>Managing Your Team</h2>
             <h3>Inviting Members</h3>
             <p>
               Expand your team by inviting colleagues or external collaborators via their email address directly within the Team settings.
             </p>
             
             <h3>Billing and Team Size</h3>
             <p>
               Adding a new member to your team increases your team size and will add a corresponding paid seat to your subscription plan. You can manage your subscription details and view current usage in the Billing settings.
             </p>

             <h2>Roles and Permissions</h2>
             <p>
               Assign specific roles to team members to control their access and capabilities within projects:
             </p>

             <h3>Owner</h3>
             <p>The Owner has complete control over the team. This includes managing all projects, billing information, team settings, and all member roles (inviting, removing, promoting, demoting). Typically, the person who created the team is the Owner.</p>

             <h3>Admin</h3>
             <p>Admins can fully manage projects, including translation keys and strings. They can also manage the review process and invite or remove other Admins and Reviewers. Admins cannot manage team-level settings like billing or delete the team.</p>

             <h3>Reviewer</h3>
             <p>Reviewers can view project data and participate actively in the <Link to="/docs/reviews">translation review process</Link>. This includes editing strings within a review and managing the approval status of keys. Reviewers cannot manage projects, keys, other team members, or settings.</p>

             <h2>Managing Roles</h2>
             <p>Only the Team Owner has the permission to change member roles, such as promoting a Reviewer to an Admin or demoting an Admin.</p>
             
           </section>
         );
    default:
      return (
        <>
          <h1>Documentation</h1>
          <p className="docs-intro">
             Welcome to the documentation. Select a feature from the sidebar to learn how to streamline your writing, translation, and collaboration workflows.
          </p>
        </>
      );
  }
};

const DocsPage: React.FC = () => {
  const { featureId = 'introduction' } = useParams<{ featureId?: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [featureId]);

  return (
    <>
      <PublicHeader />
      <div className="docs-page">
        <aside className="docs-sidebar">
          <nav>
            <ul>
              {features.map((feature) => (
                <li key={feature.id}>
                  <Link
                    to={`/docs/${feature.id}`}
                    className={featureId === feature.id ? 'active' : ''}
                  >
                    {feature.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
        <main className="docs-content">
           <FeatureContent featureId={featureId} />
        </main>
      </div>
    </>
  );
};

export default DocsPage; 